import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { AllowListItemFormSchemaValues } from 'src/features/allowList/utils/formSchema';
import { IDENTIFIER_TYPE } from 'src/features/membership/types';
import { IDENTIFIER_TYPE_TRANSLATION_MAP } from 'src/features/membership/utils/constants';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalMessages } from 'src/registry/utils/messages';
import { transliterateUppercase } from 'src/utils/regex';

type AllowListItemFormProps = {
    form: UseFormReturn<AllowListItemFormSchemaValues>;
    onSubmit: (values: AllowListItemFormSchemaValues) => void;
};

export default function AllowListItemForm({ onSubmit, form }: AllowListItemFormProps) {
    const { formatMessage } = useIntl();
    const identifierType = form.watch('identifier_type');

    React.useEffect(() => {
        if (form.formState.isDirty) {
            form.setValue('identifier_value', null);
        }
    }, [identifierType, form]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<AllowListItemFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <SelectFormField<AllowListItemFormSchemaValues>
                form={form}
                name="identifier_type"
                label={formatMessage(identifiersMessages.identifierType)}
                options={[IDENTIFIER_TYPE.LICENSE_PLATE, IDENTIFIER_TYPE.ACCESS_KEY]}
                optionTranslationMap={IDENTIFIER_TYPE_TRANSLATION_MAP}
            />
            <FormField
                control={form.control}
                name="identifier_value"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(identifiersMessages.identifier)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    {...field}
                                    onChange={(e) => {
                                        const value =
                                            identifierType === IDENTIFIER_TYPE.LICENSE_PLATE
                                                ? transliterateUppercase(e.target.value.toLocaleUpperCase())
                                                : e.target.value;
                                        field.onChange(value);
                                    }}
                                    value={field.value ?? ''}
                                    placeholder={`${formatMessage(identifiersMessages.identifier)}...`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}
